import React, { useEffect, useMemo } from "react";
import { remark } from "remark";
import remarkHtml from "remark-html";
import "./MarkdownRenderer.css";

// Helper function to generate IDs for headings
const addIdsToHeadings = (html) => {
  return html.replace(/<h([1-6])>(.*?)<\/h[1-6]>/g, (match, level, content) => {
    // Extract text content and convert to kebab-case ID
    const textContent = content.replace(/<[^>]*>/g, ""); // Remove HTML tags
    const id = textContent
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .trim();

    return `<h${level} id="${id}">${content}</h${level}>`;
  });
};

const DROP_LEADING_H1 = /^\s*<h1\b[^>]*>[\s\S]*?<\/h1>\s*/i;

// ArticleShell already renders the page title as the only h1.
const dropLeadingTitle = (html) => String(html || "").replace(DROP_LEADING_H1, "");

const markdownToHtml = (content) => {
  const result = remark().use(remarkHtml, { sanitize: true }).processSync(content);
  return dropLeadingTitle(addIdsToHeadings(String(result)));
};

/**
 * Markdown Renderer Component
 * Uses remark to convert markdown to HTML with proper styling
 */
const MarkdownRenderer = ({ content }) => {
  const htmlContent = useMemo(() => {
    if (!content) return "";
    try {
      return markdownToHtml(content);
    } catch (error) {
      return { error: error.message || "Could not render this article." };
    }
  }, [content]);

  // Handle anchor link clicks for table of contents
  useEffect(() => {
    const handleAnchorClick = (event) => {
      const target = event.target;

      // Check if clicked element is an anchor link
      if (
        target.tagName === "A" &&
        target.getAttribute("href")?.startsWith("#")
      ) {
        event.preventDefault();

        const targetId = target.getAttribute("href").substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          // Smooth scroll to the target element
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    };

    // Add event listener to the markdown content container
    const markdownContainer = document.querySelector(".markdown-content");
    if (markdownContainer) {
      markdownContainer.addEventListener("click", handleAnchorClick);

      // Cleanup event listener on unmount
      return () => {
        markdownContainer.removeEventListener("click", handleAnchorClick);
      };
    }
  }, [htmlContent]);

  if (htmlContent && typeof htmlContent === "object" && htmlContent.error) {
    return (
      <div className="prose max-w-none">
        <p className="text-red-600">Error processing markdown: {htmlContent.error}</p>
        <div className="whitespace-pre-wrap text-brand-charcoal">{content}</div>
      </div>
    );
  }

  if (!htmlContent) return null;

  return (
    <div
      className="markdown-content"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export { markdownToHtml };
export default MarkdownRenderer;
